import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Landmark, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth.store';
import { AlertBanner } from '@/components/ui/alert-banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { FormDialog } from '@/components/ui/form-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, Td, Th, THead, Tr } from '@/components/ui/table';

export interface ProvincialRate {
  id: string;
  province: string;
  authority: string;
  serviceLabel: string;
  paymentMode: string;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  sourceUrl: string;
  sourceTitle: string;
  notes: string | null;
  confirmed: boolean;
  confirmedAt: string | null;
}

export const PROVINCES = ['ICT', 'PUNJAB', 'SINDH', 'KPK', 'BALOCHISTAN'] as const;
export const AUTHORITY_OF: Record<(typeof PROVINCES)[number], string> = {
  ICT: 'FBR-ICT',
  PUNJAB: 'PRA',
  SINDH: 'SRB',
  KPK: 'KPRA',
  BALOCHISTAN: 'BRA',
};
const PAY_MODES = ['ANY', 'CASH', 'CARD'] as const;

/** Official sites, each checked to load (HTTP 200) on 2026-10-23. Shown only in the empty state. */
export const AUTHORITY_LINKS = [
  { key: 'PRA', href: 'https://pra.punjab.gov.pk' },
  { key: 'SRB', href: 'https://www.srb.gos.pk' },
  { key: 'KPRA', href: 'https://kpra.gov.pk' },
  { key: 'BRA', href: 'https://bra.gob.pk' },
] as const;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 2026-07-01 -> 01-Jul-2026 (the app's date style). */
export function formatDay(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  return m ? `${m[3]}-${MONTHS[Number(m[2]) - 1]}-${m[1]}` : '';
}

interface FormState {
  province: string;
  serviceLabel: string;
  paymentMode: string;
  rate: string;
  effectiveFrom: string;
  effectiveTo: string;
  sourceUrl: string;
  sourceTitle: string;
  notes: string;
  confirmed: boolean;
}

const EMPTY_FORM: FormState = {
  province: 'PUNJAB',
  serviceLabel: '',
  paymentMode: 'ANY',
  rate: '',
  effectiveFrom: '',
  effectiveTo: '',
  sourceUrl: 'https://',
  sourceTitle: '',
  notes: '',
  confirmed: false,
};

const selectClass = 'h-9 w-full rounded-md border bg-background px-2 text-sm';

/** POS-I197: provincial service-tax reference rates in the Store Guide (global data, shipped empty). */
export function ProvincialRatesSection() {
  const { t } = useTranslation('taxGuide');
  const qc = useQueryClient();
  const { user, hasPermission } = useAuthStore();
  const isSuperAdmin = !!user?.isSuperAdmin;
  const canOpenTaxRates = hasPermission('tax-rates:read') || hasPermission('*');

  const { data: rates = [], isLoading } = useQuery<ProvincialRate[]>({
    queryKey: ['provincial-tax-rates'],
    queryFn: () => api.get('/tax-guide/provincial-rates').then((r) => r.data),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<ProvincialRate | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ProvincialRate | null>(null);

  const openForm = (row: ProvincialRate | 'new') => {
    setFormError(null);
    setForm(
      row === 'new'
        ? EMPTY_FORM
        : {
            province: row.province,
            serviceLabel: row.serviceLabel,
            paymentMode: row.paymentMode,
            rate: String(row.rate),
            effectiveFrom: row.effectiveFrom,
            effectiveTo: row.effectiveTo ?? '',
            sourceUrl: row.sourceUrl,
            sourceTitle: row.sourceTitle,
            notes: row.notes ?? '',
            confirmed: row.confirmed,
          },
    );
    setEditing(row);
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        province: form.province,
        authority: AUTHORITY_OF[form.province as keyof typeof AUTHORITY_OF],
        serviceLabel: form.serviceLabel,
        paymentMode: form.paymentMode,
        rate: Number(form.rate),
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || null,
        sourceUrl: form.sourceUrl.trim(),
        sourceTitle: form.sourceTitle,
        notes: form.notes || null,
        confirmed: form.confirmed,
      };
      return editing && editing !== 'new'
        ? api.patch(`/platform/provincial-tax-rates/${editing.id}`, body)
        : api.post('/platform/provincial-tax-rates', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provincial-tax-rates'] });
      notify.saved(t('manual.provincial.entity'));
      setEditing(null);
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setFormError(Array.isArray(msg) ? msg[0] : msg ?? t('manual.provincial.saveFailed'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/provincial-tax-rates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provincial-tax-rates'] });
      notify.deleted(t('manual.provincial.entity'));
      setRemoving(null);
    },
    onError: (err) => notify.error(err, t('manual.provincial.saveFailed')),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    saveMutation.mutate();
  };
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const provinceName = (p: string) => t(`manual.provincial.provinces.${p}`);

  return (
    <div className="rounded-2xl border bg-card overflow-hidden print:break-inside-avoid" data-testid="provincial-rates">
      <div className="px-5 pt-5 pb-3 border-b">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-success to-success/80">
              <Landmark className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <p className="font-semibold">{t('manual.provincial.title')}</p>
          </div>
          {isSuperAdmin && (
            <Button type="button" size="sm" className="print:hidden" onClick={() => openForm('new')}>
              <Plus className="me-1.5 h-3.5 w-3.5" />
              {t('manual.provincial.addRate')}
            </Button>
          )}
        </div>
      </div>
      <div className="p-5">
        <AlertBanner tone="warning" icon={AlertTriangle}>{t('manual.provincial.banner')}</AlertBanner>

        {isLoading ? (
          <div className="h-16 animate-pulse rounded-lg bg-muted/50" aria-busy="true" />
        ) : rates.length === 0 ? (
          <div>
            <EmptyState
              icon={Landmark}
              title={t('manual.provincial.emptyTitle')}
              subtitle={t('manual.provincial.emptyBody')}
              className="py-8"
            />
            <ul className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm print:hidden">
              {AUTHORITY_LINKS.map((l) => (
                <li key={l.key}>
                  <a href={l.href} target="_blank" rel="noopener noreferrer" className="text-primary-text underline underline-offset-2">
                    {t(`manual.provincial.authorityLinks.${l.key}`)}
                  </a>
                </li>
              ))}
            </ul>
            {canOpenTaxRates && (
              <div className="mt-4 flex justify-center print:hidden">
                <Button asChild variant="outline" size="sm">
                  <Link to="/admin/master-data/tax-rates">{t('manual.provincial.goToTaxRates')}</Link>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Table className="text-xs">
              <THead>
                <Tr hover={false}>
                  <Th>{t('manual.provincial.colProvince')}</Th>
                  <Th>{t('manual.provincial.colAuthority')}</Th>
                  <Th>{t('manual.provincial.colService')}</Th>
                  <Th>{t('manual.provincial.colPayMode')}</Th>
                  <Th>{t('manual.provincial.colRate')}</Th>
                  <Th>{t('manual.provincial.colFrom')}</Th>
                  <Th>{t('manual.provincial.colTo')}</Th>
                  <Th>{t('manual.provincial.colSource')}</Th>
                  <Th>{t('manual.provincial.colConfirmed')}</Th>
                  {isSuperAdmin && <Th className="print:hidden"><span className="sr-only">{t('manual.provincial.colActions')}</span></Th>}
                </Tr>
              </THead>
              <TBody>
                {rates.map((r) => (
                  <Tr key={r.id}>
                    <Td className="px-3 py-2">{provinceName(r.province)}</Td>
                    <Td className="px-3 py-2">{r.authority}</Td>
                    <Td className="px-3 py-2 max-w-[16rem] truncate" title={r.serviceLabel}>{r.serviceLabel}</Td>
                    <Td className="px-3 py-2">{t(`manual.provincial.payModes.${r.paymentMode}`)}</Td>
                    <Td className="px-3 py-2 font-mono font-semibold"><bdi dir="ltr">{r.rate.toFixed(2)}%</bdi></Td>
                    <Td className="px-3 py-2"><bdi dir="ltr">{formatDay(r.effectiveFrom)}</bdi></Td>
                    <Td className="px-3 py-2"><bdi dir="ltr">{formatDay(r.effectiveTo)}</bdi></Td>
                    <Td className="px-3 py-2 max-w-[14rem]">
                      <a
                        href={r.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={r.sourceTitle}
                        className="block truncate text-primary-text underline underline-offset-2"
                      >
                        {r.sourceTitle}
                      </a>
                    </Td>
                    <Td className="px-3 py-2">{r.confirmed ? `${t('manual.provincial.confirmed')} ${formatDay((r.confirmedAt ?? '').slice(0, 10))}` : ''}</Td>
                    {isSuperAdmin && (
                      <Td className="px-3 py-2 print:hidden">
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="sm" aria-label={t('manual.provincial.edit')} onClick={() => openForm(r)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" variant="ghost" size="sm" aria-label={t('manual.provincial.deactivate')} onClick={() => setRemoving(r)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </TBody>
            </Table>
            {canOpenTaxRates && (
              <div className="mt-4 print:hidden">
                <Button asChild variant="outline" size="sm">
                  <Link to="/admin/master-data/tax-rates">{t('manual.provincial.goToTaxRates')}</Link>
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {isSuperAdmin && (
        <>
          <FormDialog
            open={editing !== null}
            title={editing === 'new' ? t('manual.provincial.addRate') : t('manual.provincial.edit')}
            isPending={saveMutation.isPending}
            onSubmit={submit}
            onClose={() => setEditing(null)}
          >
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pr-province">{t('manual.provincial.colProvince')}</Label>
                  <select id="pr-province" className={selectClass} value={form.province} onChange={(e) => set('province', e.target.value)}>
                    {PROVINCES.map((p) => <option key={p} value={p}>{provinceName(p)}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>{t('manual.provincial.colAuthority')}</Label>
                  <p className="flex h-9 items-center text-sm">{AUTHORITY_OF[form.province as keyof typeof AUTHORITY_OF]}</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pr-service">{t('manual.provincial.colService')}</Label>
                <Input id="pr-service" required maxLength={120} value={form.serviceLabel} onChange={(e) => set('serviceLabel', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pr-mode">{t('manual.provincial.colPayMode')}</Label>
                  <select id="pr-mode" className={selectClass} value={form.paymentMode} onChange={(e) => set('paymentMode', e.target.value)}>
                    {PAY_MODES.map((m) => <option key={m} value={m}>{t(`manual.provincial.payModes.${m}`)}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pr-rate">{t('manual.provincial.colRate')} (%)</Label>
                  <Input id="pr-rate" required type="number" inputMode="decimal" min={0} max={100} step="0.01" dir="ltr" value={form.rate} onChange={(e) => set('rate', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pr-from">{t('manual.provincial.colFrom')}</Label>
                  <Input id="pr-from" required type="date" dir="ltr" value={form.effectiveFrom} onChange={(e) => set('effectiveFrom', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pr-to">{t('manual.provincial.colTo')}</Label>
                  <Input id="pr-to" type="date" dir="ltr" value={form.effectiveTo} onChange={(e) => set('effectiveTo', e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="pr-url">{t('manual.provincial.sourceLink')}</Label>
                <Input id="pr-url" required type="text" dir="ltr" maxLength={500} value={form.sourceUrl} onChange={(e) => set('sourceUrl', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pr-title">{t('manual.provincial.sourceTitle')}</Label>
                <Input id="pr-title" required maxLength={120} value={form.sourceTitle} onChange={(e) => set('sourceTitle', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="pr-notes">{t('manual.provincial.notes')}</Label>
                <Input id="pr-notes" maxLength={500} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.confirmed} onChange={(e) => set('confirmed', e.target.checked)} />
                {t('manual.provincial.markConfirmed')}
              </label>
              {formError && <p role="alert" className="text-sm text-destructive-text text-start">{formError}</p>}
            </div>
          </FormDialog>
          <ConfirmDialog
            open={removing !== null}
            title={t('manual.provincial.deactivateTitle')}
            message={t('manual.provincial.deactivateBody')}
            confirmLabel={t('manual.provincial.deactivate')}
            variant="destructive"
            loading={removeMutation.isPending}
            onConfirm={() => removing && removeMutation.mutate(removing.id)}
            onCancel={() => setRemoving(null)}
            onClose={() => setRemoving(null)}
          />
        </>
      )}
    </div>
  );
}
